import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { getModeratorAgentsOverview } from '../utils/api';
import toast from '../utils/toastUtils';

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_AGENT_APPLICATIONS: 'SET_AGENT_APPLICATIONS',
  SET_ACTIVE_AGENTS: 'SET_ACTIVE_AGENTS',
  INVALIDATE_CACHE: 'INVALIDATE_CACHE',
  INVALIDATE_AGENT_CACHE: 'INVALIDATE_AGENT_CACHE',
};

// Initial state
const initialState = {
  agentApplications: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
    isStale: true,
  },
  activeAgents: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
    isStale: true,
  },
};

// Reducer
const moderatorDataReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        [action.dataType]: {
          ...state[action.dataType],
          loading: action.loading,
          error: action.loading ? null : state[action.dataType].error,
        },
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        [action.dataType]: {
          ...state[action.dataType],
          loading: false,
          error: action.error,
        },
      };


    case ACTIONS.SET_AGENT_APPLICATIONS:
      return {
        ...state,
        agentApplications: {
          data: action.data,
          loading: false,
          error: null,
          lastFetched: Date.now(),
          isStale: false,
        },
      };

    case ACTIONS.SET_ACTIVE_AGENTS:
      return {
        ...state,
        activeAgents: {
          data: action.data,
          loading: false,
          error: null,
          lastFetched: Date.now(),
          isStale: false,
        },
      };

    case ACTIONS.INVALIDATE_CACHE:
      return {
        ...state,
        agentApplications: { ...state.agentApplications, isStale: true },
        activeAgents: { ...state.activeAgents, isStale: true },
      };

    case ACTIONS.INVALIDATE_AGENT_CACHE:
      return {
        ...state,
        agentApplications: { ...state.agentApplications, isStale: true },
        activeAgents: { ...state.activeAgents, isStale: true },
      };

    default:
      return state;
  }
};

// Context
const ModeratorDataContext = createContext();

// Cache duration (15 minutes - moderator data can be cached longer)
const CACHE_DURATION = 15 * 60 * 1000;

// Provider component
export const ModeratorDataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(moderatorDataReducer, initialState);

  // Helper function to check if data is stale
  const isDataStale = useCallback((lastFetched) => {
    if (!lastFetched) return true;
    return Date.now() - lastFetched > CACHE_DURATION;
  }, []);


  const fetchAgentsOverview = useCallback(
    async (forceRefresh = false) => {
      const { agentApplications, activeAgents } = state;

      const agentApplicationsFresh =
        agentApplications.data &&
        !agentApplications.isStale &&
        !isDataStale(agentApplications.lastFetched);
      const activeAgentsFresh =
        activeAgents.data && !activeAgents.isStale && !isDataStale(activeAgents.lastFetched);

      if (!forceRefresh && agentApplicationsFresh && activeAgentsFresh) {
        return {
          agentApplications: agentApplications.data,
          activeAgents: activeAgents.data,
        };
      }

      if (agentApplications.loading || activeAgents.loading) {
        return {
          agentApplications: agentApplications.data,
          activeAgents: activeAgents.data,
        };
      }

      dispatch({ type: ACTIONS.SET_LOADING, dataType: 'agentApplications', loading: true });
      dispatch({ type: ACTIONS.SET_LOADING, dataType: 'activeAgents', loading: true });

      try {
        const data = await getModeratorAgentsOverview();
        const applications = data?.agentApplications || [];
        const agents = data?.activeAgents || [];

        dispatch({ type: ACTIONS.SET_AGENT_APPLICATIONS, data: applications });
        dispatch({ type: ACTIONS.SET_ACTIVE_AGENTS, data: agents });

        return {
          agentApplications: applications,
          activeAgents: agents,
        };
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Failed to fetch agents overview';

        dispatch({
          type: ACTIONS.SET_ERROR,
          dataType: 'agentApplications',
          error: errorMessage,
        });
        dispatch({
          type: ACTIONS.SET_ERROR,
          dataType: 'activeAgents',
          error: errorMessage,
        });

        toast.error(errorMessage);
        throw error;
      }
    },
    [state, isDataStale]
  );

  // Fetch agent applications with caching
  const fetchAgentApplications = useCallback(
    async (forceRefresh = false) => {
      const data = await fetchAgentsOverview(forceRefresh);
      return data.agentApplications;
    },
    [fetchAgentsOverview]
  );

  // Fetch active agents with caching
  const fetchActiveAgents = useCallback(
    async (forceRefresh = false) => {
      const data = await fetchAgentsOverview(forceRefresh);
      return data.activeAgents;
    },
    [fetchAgentsOverview]
  );

  // Cache invalidation methods
  const invalidateAllCache = useCallback(() => {
    dispatch({ type: ACTIONS.INVALIDATE_CACHE });
  }, []);

  const invalidateAgentCache = useCallback(() => {
    dispatch({ type: ACTIONS.INVALIDATE_AGENT_CACHE });
  }, []);

  // Method to call when agent-related actions are performed
  const onAgentActionCompleted = useCallback(() => {
    invalidateAgentCache();
  }, [invalidateAgentCache]);

  const value = {
    // State
    agentApplications: state.agentApplications,
    activeAgents: state.activeAgents,
    
    // Fetch methods
    fetchAgentApplications,
    fetchActiveAgents,
    
    // Cache invalidation
    invalidateAllCache,
    invalidateAgentCache,
    
    // Event handlers for cache invalidation
    onAgentActionCompleted,
  };

  return (
    <ModeratorDataContext.Provider value={value}>
      {children}
    </ModeratorDataContext.Provider>
  );
};

// Hook to use the context
export const useModeratorData = () => {
  const context = useContext(ModeratorDataContext);
  if (!context) {
    throw new Error('useModeratorData must be used within a ModeratorDataProvider');
  }
  return context;
};

export default ModeratorDataContext;
